import * as yup from 'yup';

import locales from '../locales';
import { REGEX } from '../regex';
import { FORM_INPUT_ERROR_MSGS } from '../errors';
import { isValid } from '../helpers/dates';
import { helperValidExample as msisdnValidExample } from '../../components/common/fields/msisdn-input/const';

export const merchantCodeValidation = yup
  .string()
  .matches(REGEX.MERCHANT_CODE, FORM_INPUT_ERROR_MSGS.EXACT('Merchand code', 8, 'digits'));

export const otpEmailValidation = yup
  .string()
  .matches(REGEX.OTP, FORM_INPUT_ERROR_MSGS.EXACT('OTP Email code', 6))
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED(locales.otp.label.email));

export const otpSmsValidation = yup
  .string()
  .matches(REGEX.OTP, FORM_INPUT_ERROR_MSGS.EXACT('OTP code', 6))
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED(locales.otp.label.sms));

export const emailValidation = yup
  .string()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED(locales.accountInformation.label.email))
  .email(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT(locales.accountInformation.label.email, 'address@example.com'));

export const balanceThresholdValidation = yup
  .string()
  .matches(
    REGEX.CURRENCY_NUMBER,
    FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('balance threshold', 'amount', '100, 100.0, 100.00')
  )
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED('balance threshold'));

export const nameValidation = yup
  .string()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED(locales.accountInformation.label.name))
  .max(50, FORM_INPUT_ERROR_MSGS.MAX);

export const countryValidation = yup
  .string()
  .trim()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED(locales.accountInformation.label.country))
  .max(30, FORM_INPUT_ERROR_MSGS.MAX);

export const msisdnValidationWithPrefix = yup
  .string()
  .matches(
    REGEX.MSISDN_WITH_PREFIX,
    FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('phone number', 'ten digit', msisdnValidExample)
  );

export const msisdnValidationWithoutPrefix = yup
  .string()
  .matches(
    REGEX.MSISDN_WITHOUT_PREFIX,
    FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('phone number', 'ten digit', msisdnValidExample)
  );

export const msisdnValidationWithoutPrefixRequired = yup
  .string()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED('Phone number'))
  .matches(
    REGEX.MSISDN_WITHOUT_PREFIX,
    FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('phone number', 'ten digit', msisdnValidExample)
  );

export const amountValidation = yup
  .string()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED('amount'))
  .matches(REGEX.AMOUNT, FORM_INPUT_ERROR_MSGS.AMOUNT());

export const msisdnValidationSimpleRequired = yup
  .string()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED('Phone number'))
  .matches(REGEX.MSISDN_SIMPLE, FORM_INPUT_ERROR_MSGS.MSISDN_SIMPLE());

export const msisdnValidationSimple = yup.string().matches(REGEX.MSISDN_SIMPLE, FORM_INPUT_ERROR_MSGS.MSISDN_SIMPLE());

export const callbackValidation = yup
  .string()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED(locales.accountInformation.label.callback))
  .url(
    FORM_INPUT_ERROR_MSGS.INVALID_FORMAT(
      locales.accountInformation.label.callback,
      'URL',
      'https://myserver.com/send/callback/here'
    )
  );

export const appUrlValidation = yup
  .string()
  .url(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('App URL', 'URL', 'https://myapp.com/param/xyz/'))
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED('App URL'));

export const appStatusValidation = yup.string().required(FORM_INPUT_ERROR_MSGS.REQUIRED('Status'));
export const debugEnabledValidation = yup.string().required(FORM_INPUT_ERROR_MSGS.REQUIRED('Debug mode'));
export const authorizationMethodValidation = yup.string().required(FORM_INPUT_ERROR_MSGS.REQUIRED('Authentication'));
export const externalAddressValidation = yup.string().required(FORM_INPUT_ERROR_MSGS.REQUIRED('External Address'));

export const smsTemplateValidation = yup
  .string()
  .test(
    'containsLinkInBrackets',
    FORM_INPUT_ERROR_MSGS.SMS_TEMPLATE,
    value => value !== undefined && value.includes('{link}')
  )
  .required();

const billingEndNowValueSelected = yup
  .string()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED('to Date'))
  .when('from', function (from) {
    if (isValid(from)) {
      return yup.string().test('fromDateAfterTo', FORM_INPUT_ERROR_MSGS.END_DATE_BEFORE_START, function (this) {
        return this.parent.from.getTime() <= new Date().getTime();
      });
    }
    return this;
  });

const billingEndDate = yup
  .date()
  .required()
  .typeError(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('to date'))
  .when('from', function (from) {
    if (isValid(from)) {
      return yup.date().min(from, FORM_INPUT_ERROR_MSGS.END_DATE_BEFORE_START);
    }
    return this;
  });

export const billingEndDateValidation = yup.lazy(value =>
  value === 'now' ? billingEndNowValueSelected : billingEndDate
);

export const reportToDate = yup
  .date()
  .required(FORM_INPUT_ERROR_MSGS.REQUIRED('End date'))
  .typeError(FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('End date'))
  .when('from', function (from) {
    if (isValid(from)) {
      return yup.date().min(from, FORM_INPUT_ERROR_MSGS.END_DATE_BEFORE_START);
    }
    return this;
  });

export const shortcode = yup.array(
  yup
    .string()
    .matches(REGEX.SHORTCODE, FORM_INPUT_ERROR_MSGS.INVALID_FORMAT('shortcode', REGEX.SHORTCODE, '123 or 123*456'))
);

export const paymentMethodValidation = yup.string().required(FORM_INPUT_ERROR_MSGS.REQUIRED_SELECT('payment method'));
